/**
 * Delivery Locations and Charges for Sky Furniture Canada
 * All prices in CAD
 * Organized by delivery charge tiers
 */

export const deliveryCharges = {
  // Free Delivery - $0
  0: ['Etobicoke'],
  
  // Tier 1 - $10
  10: [
    'Bramalea',
    'Brampton',
    'Malton',
    'Mississauga',
    'North York',
    'Toronto'
  ],
  
  // Tier 2 - $20
  20: [
    'Ajax',
    'Caledon',
    'East York',
    'Markham',
    'Oshawa',
    'Pickering',
    'Richmond Hill',
    'Scarborough',
    'Thornhill',
    'Unionville',
    'Vaughan',
    'Whitby'
  ],
  
  // Tier 3 - $30
  30: [
    'Bolton',
    'Burlington',
    'Caledon East',
    'Glen Abbey',
    'Halton Hills',
    'Milton',
    'Oakville'
  ],
  
  // Tier 4 - $40
  40: [
    'Calgary',
    'Edmonton',
    'Red Deer',
    'Vancouver',
    'Surrey',
    'Burnaby',
    'Montreal',
    'Quebec City',
    'Laval',
    'Longueuil',
    'Brossard',
    'Châteauguay',
    'Saint-Lambert',
    'Acton',
    'Aldershot',
    'Aurora',
    'Barrie',
    'Bowmanville',
    'Bradford West Gwillimbury',
    'Brant',
    'Brantford',
    'Cambridge',
    'East Gwillimbury',
    'Erin',
    'Georgetown',
    'Guelph',
    'Hamilton',
    'Innisfil',
    'King',
    'King City',
    'Kitchener',
    'Newmarket',
    'Nobleton',
    'Orangeville',
    'Puslinch',
    'Waterloo',
    'Whitchurch-Stouffville'
  ],
  
  // Tier 5 - $50
  50: [
    'Amaranth',
    'Centre Wellington',
    'Courtice',
    'East Garafraxa',
    'Essa',
    'Georgina',
    'Grimsby',
    'Guelph/Eramosa',
    'Lincoln',
    'Melancthon',
    'Mono',
    'New Tecumseth',
    'Niagara Falls',
    'North Dumfries',
    'Pelham',
    'Scugog',
    'Shelburne',
    'St. Catharines',
    'Welland',
    'West Lincoln',
    'Wilmot',
    'Windsor',
    'Woodstock',
    'Woolwich'
  ],
  
  // Tier 6 - $60
  60: [
    'Ingersoll',
    'London',
    'Port Perry',
    'Tecumseh',
    'Uxbridge',
    'Wellesley'
  ],
  
  // Tier 7 - $70
  70: [
    'Blandford-Blenheim',
    'Fort Erie',
    'Kingsville',
    'LaSalle',
    'Leamington',
    'Newcastle',
    'Niagara-on-the-Lake',
    'Wainfleet',
    'Wellington North'
  ],
  
  // Tier 8 - $80
  80: [
    'Aylmer',
    'Clarington',
    'East Zorra-Tavistock',
    'Haldimand',
    'Kawartha Lakes',
    'Norwich',
    'Ottawa',
    'Springwater',
    'Stratford',
    'Sunderland',
    'Tillsonburg'
  ],
  
  // Tier 9 - $90
  90: [
    'Beaverton',
    'Brock',
    'St. Thomas'
  ],
  
  // Tier 10 - $100
  100: [
    'Amherstburg',
    'Belleville',
    'Clearview',
    'Collingwood',
    'Kingston',
    'Midland',
    'Norfolk County',
    'Orillia',
    'Owen Sound',
    'Penetanguishene',
    'Peterborough',
    'Quinte West',
    'Simcoe',
    'Tay',
    'Tiny',
    'Wasaga Beach'
  ]
};

/**
 * Get delivery charge for a city
 * @param {string} city - City name
 * @returns {number} - Delivery charge in CAD
 */
export const getDeliveryCharge = (city) => {
  if (!city) return null;
  
  const cityLower = city.toLowerCase().trim();
  
  // Search through all tiers
  for (const [charge, cities] of Object.entries(deliveryCharges)) {
    const found = cities.find(c => c.toLowerCase() === cityLower);
    if (found) {
      return parseInt(charge);
    }
  }
  
  // City not found
  return null;
};

/**
 * Get all cities as flat array (for autocomplete/dropdown)
 * @returns {Array} - Array of city names sorted alphabetically
 */
export const getAllCities = () => {
  const allCities = [];
  
  Object.values(deliveryCharges).forEach(cities => {
    allCities.push(...cities);
  });
  
  return allCities.sort();
};

/**
 * Search cities by partial match
 * @param {string} query - Search query
 * @returns {Array} - Matching cities
 */
export const searchCities = (query) => {
  if (!query || query.length < 1) return [];
  
  const queryLower = query.toLowerCase();
  const allCities = getAllCities();
  
  return allCities.filter(city => 
    city.toLowerCase().includes(queryLower)
  ).slice(0, 10); // Return max 10 results
};

/**
 * Validate if city is serviced
 * @param {string} city - City name
 * @returns {boolean} - True if city is in delivery area
 */
export const isCityServiced = (city) => {
  return getDeliveryCharge(city) !== null;
};

/**
 * Get cities grouped by delivery charge (for display purposes)
 * @returns {Object} - Cities grouped by charge
 */
export const getCitiesByCharge = () => {
  const grouped = {};
  
  Object.entries(deliveryCharges).forEach(([charge, cities]) => {
    grouped[`$${charge}`] = cities;
  });
  
  return grouped;
};